/** biome-ignore-all lint/a11y/noStaticElementInteractions: <valid use for contenteditable> */
import { HStack, Stack, VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import {
	children,
	createSignal,
	type JSX,
	mergeProps,
	onCleanup,
	onMount,
	Show,
} from "solid-js";
import { animateButtonRowSwitch } from "./animateButtonRowSwitch";
import {
	QuestionContext,
	type QuestionContextAnswer,
	type QuestionContextItems,
	type QuestionContextPool,
} from "./QuestionContext";
import type { QuestionContextChannel } from "./QuestionContextChannels";
import {
	QuestionContextSelector,
	type QuestionContextSelectorTab,
} from "./QuestionContextSelector";
import type { QuestionContextVideo } from "./QuestionContextVideos";
import "./QuestionBox.css";
import { BillingCreditsBalance } from "~/modules/askgvt/studio/ask-question/questionBuilder/libraryView/BillingCreditsBalance";
import { isSignedIn } from "~/modules/shared/utils/auth";
import { hasNoCreditsLeft } from "~/modules/shared/utils/useMeteringBalance";
import { AskQuestionSubmitButton } from "./AskQuestionSubmitButton";
import { ListenToUserButton } from "./ListenToUserButton";
import {
	type QuestionBoxCreator,
	QuestionBoxCreators,
} from "./QuestionBoxCreators";
import { QuestionBoxEffort } from "./QuestionBoxEffort";
import type { QuestionBoxVideoMentionData } from "./QuestionBoxVideoMention";
import { defaultQuestionEffort, type QuestionEffort } from "./questionEfforts";
import { saveRecentAnswerContext } from "./RecentAnswerContexts";
import {
	type QuestionBoxHandle,
	type QuestionBoxProps,
	type QuestionBoxScope,
	useQuestionBox,
} from "./useQuestionBox";

export type {
	QuestionBoxCreator,
	QuestionBoxHandle,
	QuestionBoxProps,
	QuestionBoxScope,
	QuestionBoxVideoMentionData,
	QuestionContextChannel,
	QuestionContextSelectorTab,
	QuestionContextVideo,
	QuestionEffort,
};

/**
 * What the question is asked of, for controls outside the box that choose it.
 * The box owns this state because submitting and mention search read it.
 */
export type QuestionBoxScoping = {
	selectedProjectId: () => string;
	setSelectedProjectId: (id: string) => void;
	selectedPoolId: () => string;
	setSelectedPoolId: (id: string) => void;
	/** Names a pool that isn't in `publicPools`, for the context row. */
	setSelectedPoolName: (name: string) => void;
	/**
	 * True while the question mentions an account, which was looked up in the
	 * current scope and would not survive a change of it.
	 */
	scopeLocked: () => boolean;
};

/** The pieces of a QuestionBox, for a caller that lays them out itself. */
export type QuestionBoxParts = {
	/**
	 * The bordered input, with its children above the question. Render it
	 * exactly once: the box binds to its input when it mounts.
	 */
	Field: (props: { children?: JSX.Element }) => JSX.Element;
	/** The effort control, shown only when the box has `onEffortChange`. */
	Effort: () => JSX.Element;
};

export const QuestionBox = (
	props: QuestionBoxProps & {
		rounded?: boolean;
		showListenToUserButton?: boolean;
		/**
		 * Renders the controls that choose the project or pool, under the input.
		 * A slot rather than built in, so that the Studio-only selectors stay
		 * out of every bundle that has no use for them.
		 */
		selectors?: (scoping: QuestionBoxScoping) => JSX.Element;
		/**
		 * Lays the box out from its parts, in place of the input followed by the
		 * context row.
		 */
		children?: (parts: QuestionBoxParts) => JSX.Element;
		/**
		 * The creators an Ask question goes to; empty means all of the viewer's
		 * subscriptions. Given, it takes the place of the Studio context row,
		 * which describes projects and pools.
		 */
		creators?: QuestionBoxCreator[];
		/** Shown with "All subscriptions" when `creators` is empty. */
		subscriptionCount?: number;
		/** Overrides the creators with why there is no creator to ask. */
		creatorsNotice?: string;
		/** Secondary detail, at the far end of the creators row. */
		contextDetail?: string;
		/**
		 * While true, the context row shows only its effort control, for a box
		 * whose scope is still being looked up.
		 */
		contextPending?: boolean;
		/** Given, the context row ends with a control for choosing the effort. */
		onEffortChange?: (effort: QuestionEffort) => void;
		/** Given, the box is disabled and says why above the input. */
		disabledReason?: string;
		/**
		 * Whether a question asked here spends the viewer's own credits. While it
		 * does and none are left, the box is disabled under the out-of-credits
		 * banner. @default isSignedIn()
		 */
		chargesViewerCredits?: boolean;
	},
) => {
	const chargesViewerCredits = () => props.chargesViewerCredits ?? isSignedIn();
	const outOfCredits = () => chargesViewerCredits() && hasNoCreditsLeft();

	const initialContextItems = (): QuestionContextItems => {
		const seeded = props.initialContextAnswer;
		if (seeded == null) {
			return { answers: [], files: [] };
		}
		return { answers: [seeded], files: [] };
	};

	const [questionContext, setQuestionContext] =
		createSignal<QuestionContextItems>(initialContextItems());

	let inputRow: HTMLDivElement | undefined;
	onMount(() => {
		if (inputRow) {
			onCleanup(animateButtonRowSwitch(inputRow));
		}
	});

	onMount(() => {
		const id = props.initialContextAnswer?.id?.trim();
		if (id) {
			saveRecentAnswerContext(id);
		}
	});

	// mergeProps, not an object spread: a spread reads every prop once, so
	// the hook would never see `disabled` (or anything else) change.
	const box = useQuestionBox(
		mergeProps(props, {
			get disabled() {
				return (
					props.disabled || props.disabledReason !== undefined || outOfCredits()
				);
			},
			get placeholder() {
				return outOfCredits()
					? "Out of question credits, top up or upgrade to continue"
					: props.placeholder;
			},
			getContextArtifactId() {
				const fromRow = questionContext().answers.find((answer) =>
					answer.artifactId?.trim(),
				)?.artifactId;
				return fromRow?.trim() ?? props.contextArtifactId?.trim();
			},
		}),
	);

	const addContextAnswer = (answer: QuestionContextAnswer) => {
		saveRecentAnswerContext(answer.id);
		setQuestionContext((prev) => {
			if (prev.answers.some((item) => item.id === answer.id)) {
				return prev;
			}
			return { ...prev, answers: [...prev.answers, answer] };
		});
	};

	const removeContextAnswer = (id: string) => {
		setQuestionContext((prev) => ({
			...prev,
			answers: prev.answers.filter((item) => item.id !== id),
		}));
	};

	const contextAnswerIds = () =>
		questionContext().answers.map((answer) => answer.id);

	const [projectPoolName, setProjectPoolName] = createSignal(
		props.poolName ?? "",
	);

	const scoping: QuestionBoxScoping = {
		selectedProjectId: box.selectedProjectId,
		setSelectedProjectId: (id) => box.setSelectedProjectId(id),
		selectedPoolId: box.selectedPoolId,
		setSelectedPoolId: (id) => box.setSelectedPoolId(id),
		setSelectedPoolName: (name) => setProjectPoolName(name),
		scopeLocked: box.isProjectSelectorDisabled,
	};

	const namedPublicPool = (id: string) =>
		(props.publicPools ?? []).find((pool) => pool.id === id);

	const contextPool = (): QuestionContextPool | undefined => {
		const selectedId = box.selectedPoolId().trim();
		if (props.scope === "influencer_pool") {
			return { kind: "own" };
		}
		if (props.scope === "public_pool") {
			const designatedId = (props.defaultPoolId ?? "").trim();
			const named = namedPublicPool(selectedId);
			if (
				selectedId.length > 0 &&
				designatedId.length > 0 &&
				selectedId !== designatedId &&
				named
			) {
				return { kind: "other", name: named.name };
			}
			return { kind: "public" };
		}
		if (selectedId.length === 0) {
			return undefined;
		}
		const named = namedPublicPool(selectedId);
		return {
			kind: "other",
			name: named?.name ?? projectPoolName(),
		};
	};

	const effortControl = () => (
		<Show when={props.onEffortChange}>
			{(onEffortChange) => (
				<QuestionBoxEffort
					value={props.effort ?? defaultQuestionEffort()}
					onChange={onEffortChange()}
					disabled={box.isInputDisabled()}
				/>
			)}
		</Show>
	);

	const Field = (fieldProps: { children?: JSX.Element }) => {
		// Resolved once: reading a JSX prop again would build its elements again.
		const header = children(() => fieldProps.children);
		return (
			<form onSubmit={(e) => e.preventDefault()}>
				<div
					class="gvtui-question-box input-container"
					classList={{
						"gvtui-question-box--reply": box.variant() === "reply",
						disabled: box.isDisabled(),
					}}
				>
					<Show when={header()}>
						<div class="gvtui-question-box__header">{header()}</div>
					</Show>
					<Stack
						direction="row"
						align="start"
						spacing="small"
						class="input-container-content"
					>
						<HStack
							ref={(el) => {
								inputRow = el;
							}}
							class="gvtui-question-context-focus-scope gvtui-question-box__input-row"
							fullWidth
							align={box.inputRowAlign()}
							spacing="medium"
						>
							<div
								class="question-container"
								onMouseDown={(event) => event.stopPropagation()}
							>
								<Placeholder
									hideHelpText={box.hideHelpText()}
									text={box.placeholderText()}
								/>
								<div
									contenteditable={box.isInputDisabled() ? "false" : "true"}
									ref={box.assignInputRef}
									onInput={box.handleInput}
									on:beforeinput={box.handleBeforeInput}
									on:compositionstart={box.handleCompositionStart}
									on:compositionend={box.handleCompositionEnd}
									onKeyDown={box.handleKeyDown}
									onPaste={box.handlePaste}
									onCopy={box.handleCopy}
									onCut={box.handleCut}
									class="question"
									role="textbox"
									aria-multiline="true"
									aria-expanded={
										props.mentions !== false && box.contextSelectorOpen()
											? true
											: undefined
									}
									aria-controls={
										props.mentions !== false && box.contextSelectorOpen()
											? "question-context-selector-popover"
											: undefined
									}
									style={
										props.maxLines != null
											? {
													"max-height": `calc(${props.maxLines}em * var(--line-height-default))`,
												}
											: undefined
									}
								/>
							</div>
							<HStack
								class="gvtui-question-box__actions"
								align={box.inputRowAlign()}
								spacing="medium"
							>
								<Show when={props.mentions !== false}>
									<QuestionContextSelector
										tabs={props.contextTabs}
										channelsTabLabel={props.channelsTabLabel}
										requestedTab={() =>
											box.contextSelectorOpen() ? "channels" : null
										}
										highlightedIndex={box.highlightedIndex()}
										onHighlightRow={box.setHighlightedIndex}
										registerNavigator={box.registerContextNavigator}
										beginMention={box.beginContextMention}
										onDismiss={box.dismissContextMention}
										videos={props.contextVideos ?? []}
										mentionQuery={box.mentionQuery() ?? ""}
										browsesPool={box.browsesContextPool()}
										searchVideos={box.searchContextVideos}
										insertedVideoMentionKeys={box.insertedVideoMentionKeys}
										insertVideoMentions={(mentions) =>
											box.pickFromContext(() =>
												box.insertVideoMentions(mentions),
											)
										}
										channels={props.contextChannels ?? []}
										channelCompletions={box.completions()}
										onSelectChannelCompletion={(account) =>
											box.pickFromContext(() => box.acceptCompletion(account))
										}
										insertedAccountMentionKeys={box.insertedAccountMentionKeys}
										insertAccountMentions={(mentions) =>
											box.pickFromContext(() =>
												box.insertAccountMentions(mentions),
											)
										}
										contextAnswerIds={contextAnswerIds}
										onAddContextAnswer={(answer) =>
											box.pickFromContext(() => {
												addContextAnswer(answer);
												box.removeMentionAtCaret();
											})
										}
										contextQuestionId={props.contextQuestionId}
										disabled={box.isInputDisabled()}
									/>
								</Show>
								<HStack
									class="gvtui-question-context-focus-scope__outside"
									align={box.inputRowAlign()}
									spacing="medium"
								>
									<Show when={props.showListenToUserButton}>
										<ListenToUserButton
											disabled={box.isInputDisabled()}
											onBeginSession={box.beginSpeechSession}
											onEndSession={box.endSpeechSession}
											onTranscript={box.applySpeechTranscript}
										/>
									</Show>
									<AskQuestionSubmitButton
										isLoading={props.isLoading}
										requiresPoolSelection={props.requiresPoolSelection}
										selectedPoolId={box.selectedPoolId()}
										publicPools={props.publicPools}
										question={box.question()}
										isDisabled={box.isDisabled()}
										onSubmit={box.submit}
										buttonLabel={props.buttonLabel}
										buttonSymbol={props.buttonSymbol}
										buttonTheme={props.buttonTheme}
										allowEmpty={props.onEmptySubmit !== undefined}
										theme={props.theme}
										rounded={props.rounded}
										variant={box.variant()}
									/>
								</HStack>
							</HStack>
						</HStack>
					</Stack>
					{props.selectors?.(scoping)}
				</div>
			</form>
		);
	};

	const contextRow = () => (
		<Show
			when={!props.contextPending}
			fallback={
				<HStack fullWidth justify="end">
					{effortControl()}
				</HStack>
			}
		>
			<Show
				when={props.creators}
				fallback={
					<Show when={isSignedIn()}>
						<QuestionContext
							items={questionContext()}
							onRemoveAnswer={removeContextAnswer}
							selectedPool={contextPool()}
							trailing={effortControl()}
						/>
					</Show>
				}
			>
				{(creators) => (
					<QuestionBoxCreators
						creators={creators()}
						subscriptionCount={props.subscriptionCount}
						notice={props.creatorsNotice}
						detail={props.contextDetail}
						trailing={effortControl()}
					/>
				)}
			</Show>
		</Show>
	);

	const parts: QuestionBoxParts = { Field, Effort: effortControl };

	return (
		<>
			<Show when={chargesViewerCredits()}>
				<BillingCreditsBalance />
			</Show>
			<div
				ref={box.setWrapperRef}
				onMouseUp={box.handleWrapperMouseUp}
				class="gvtui-question-box-wrapper"
				classList={{
					"gvtui-question-box-wrapper--labs": props.theme === "labs",
					"gvtui-question-box-wrapper--studio": props.theme === "studio",
					"gvtui-question-box-wrapper--rounded": props.rounded,
				}}
			>
				<VStack spacing="medium">
					<Show when={props.disabledReason}>
						{(reason) => (
							<Text size="small" color="error">
								{reason()}
							</Text>
						)}
					</Show>
					<Show
						when={props.children}
						fallback={
							<>
								<Field />
								{contextRow()}
							</>
						}
					>
						{(layout) => layout()(parts)}
					</Show>
				</VStack>
			</div>
		</>
	);
};

const Placeholder = (props: { hideHelpText: boolean; text?: string }) => {
	return (
		<div classList={{ "question-placeholder": true, hide: props.hideHelpText }}>
			{props.text ?? "Ask a question"}
		</div>
	);
};
